import { DownOutlined } from '@ant-design/icons';
import { Empty, Spin } from 'antd';
import cn from 'classnames';
import {
  type FC,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLeadCardContext } from '@app/components/LeadCard/context';
import type { LeadCardHistoryItemData } from '@app/components/LeadCard/data';
import { LeadPinnedAudioPlayer } from '@app/components/LeadCard/ui/MessageHistory/components/LeadAudioPlayer';
import { LeadCommentMessage } from '@app/components/LeadCard/ui/MessageHistory/components/LeadCommentMessage';
import { LeadMessageItem } from '@app/components/LeadCard/ui/MessageHistory/components/LeadMessageItem';
import { useRecommendVisibleRead } from '@app/components/LeadCard/ui/MessageHistory/hooks/useRecommendVisibleRead';
import {
  buildLeadMessageGroupFlags,
  getLeadHistoryStableKey,
  LEAD_COMMENT_OBJECT_TYPE,
  scrollToLeadMessage,
} from '@app/components/LeadCard/ui/MessageHistory/lib';
import { getRecommendMessageKey } from '@app/components/LeadCard/ui/MessageHistory/lib/recommendReadBatches';
import {
  leadAudioPlayback,
  useLeadAudioPlayback,
} from '@app/components/LeadCard/ui/MessageHistory/model/leadAudioPlayback';

import './LeadMessageList.scss';

const SCROLL_BOTTOM_TOLERANCE = 6;
const SCROLL_TOP_TOLERANCE = 2;

export const LeadMessageList: FC = () => {
  const {
    selectedLeadHistoryItems,
    filteredLeadHistoryItems,
    pinnedCommentIdsAheadOfHistory,
    isLeadMessageHistoryInitialized,
    isLeadMessageHistoryCurrent,
    isOlderHistoryLoading,
    isAllHistoryLoading,
    isHistoryFullyLoaded,
    canLoadOlderHistory,
    olderHistoryLoadFailed,
    ensureHistoryFitsViewport,
    loadOlderHistory,
    loadAllHistory,
    scrollContainerRef,
    scrollToBottom,
    selectedLead,
    selectedLeadId,
    isMessagesLoading,
    refetchLeadCard,
    refreshLatestHistory,
  } = useLeadCardContext();

  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasScrollableContent, setHasScrollableContent] = useState(false);
  const {
    pendingKeys,
    readError,
    registerReadElementHandler,
    retryReadHandler,
  } = useRecommendVisibleRead({
    items: filteredLeadHistoryItems,
    allItems: selectedLeadHistoryItems,
    scrollContainerRef,
    selectedLeadId,
    isEnabled: isLeadMessageHistoryCurrent && !isMessagesLoading,
    onReadAccepted: refetchLeadCard,
    refreshLatestHistory,
  });

  // Закреплённые комментарии старше загруженной истории стоят стопкой над
  // лентой, остальное — в потоке. Иначе на верху списка стопка разворачивалась
  // в начале каждой подгруженной порции (issue/11369).
  const { aheadPinnedCommentItems, flowItems } = useMemo(() => {
    const isAheadPinnedComment = ({
      ObjectType,
      ObjectId,
    }: LeadCardHistoryItemData) =>
      ObjectType === LEAD_COMMENT_OBJECT_TYPE &&
      pinnedCommentIdsAheadOfHistory.has(ObjectId);

    return {
      aheadPinnedCommentItems:
        filteredLeadHistoryItems.filter(isAheadPinnedComment),
      flowItems: filteredLeadHistoryItems.filter(
        (item) => !isAheadPinnedComment(item)
      ),
    };
  }, [filteredLeadHistoryItems, pinnedCommentIdsAheadOfHistory]);

  // Соседей сообщения знает только список, поэтому флаги группы считаются
  // здесь и уезжают в LeadMessageItem пропсами.
  const groupFlags = useMemo(
    () => buildLeadMessageGroupFlags(flowItems),
    [flowItems]
  );

  // Запись принадлежит конкретному лиду: при переключении карточки и при уходе
  // с неё закреплённый плеер снимается вместе со звуком. Состояние
  // воспроизведения живёт в модульном сторе вне дерева React, поэтому само оно
  // не сбросится — играло бы аудио чужого лида, а на время загрузки истории
  // плеер ещё и прячется под скелетоном, то есть без кнопок управления.
  // Глушим только СВОЮ запись: история монтируется и вложенно (карточка лида
  // в модалке записи), и её закрытие не должно обрывать соседнюю.
  useEffect(
    () => () => leadAudioPlayback.stopForLead(selectedLeadId),
    [selectedLeadId]
  );

  // Закреп перекрывает верх списка: пока он на экране, кнопка «Загрузить всю
  // историю» и баннер ошибки загрузки сдвигаются ниже (см. SCSS).
  const hasPinnedAudio = useLeadAudioPlayback().fileId !== null;

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const nextHasScrollableContent =
      container.scrollHeight - container.clientHeight > SCROLL_BOTTOM_TOLERANCE;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;

    setHasScrollableContent(nextHasScrollableContent);
    setIsAtBottom(
      !nextHasScrollableContent || distanceFromBottom <= SCROLL_BOTTOM_TOLERANCE
    );
  }, [scrollContainerRef]);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollState();

    if (
      container.scrollTop <= SCROLL_TOP_TOLERANCE &&
      canLoadOlderHistory &&
      isLeadMessageHistoryInitialized &&
      !isOlderHistoryLoading
    ) {
      void loadOlderHistory();
    }
  };

  // A reply preview (marked with data-reply-target-id) scrolls the list to the
  // original message and briefly highlights it, Telegram-style. Handled via
  // delegation so every message type stays presentational.
  const handleReplyClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const replyElement = (event.target as HTMLElement).closest<HTMLElement>(
        '[data-reply-target-id]'
      );
      const targetMessageId = replyElement?.dataset.replyTargetId;
      if (!targetMessageId) return;

      scrollToLeadMessage(scrollContainerRef.current, targetMessageId);
    },
    [scrollContainerRef]
  );

  const scrollToBottomClickHandler = useCallback(() => {
    const container = scrollContainerRef.current;

    if (!container) {
      scrollToBottom();
      return;
    }

    container.scrollTo({
      top: container.scrollHeight - container.clientHeight,
      behavior: 'smooth',
    });
  }, [scrollContainerRef, scrollToBottom]);

  // Без скроллбара scroll-события не приходят, поэтому догрузка старой
  // истории для короткого списка запускается отсюда; гарды
  // ensureHistoryFitsViewport не дают ей задвоиться, а после ошибки загрузки
  // останавливают автоподгрузку до ручного «Повторить».
  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      updateScrollState();
      void ensureHistoryFitsViewport();
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [
    ensureHistoryFitsViewport,
    filteredLeadHistoryItems.length,
    isMessagesLoading,
    isOlderHistoryLoading,
    selectedLeadHistoryItems.length,
    updateScrollState,
  ]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const content = container?.firstElementChild;

    if (!container || typeof ResizeObserver === 'undefined') return undefined;

    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
      void ensureHistoryFitsViewport();
    });
    resizeObserver.observe(container);

    if (content) {
      resizeObserver.observe(content);
    }

    return () => resizeObserver.disconnect();
  }, [
    ensureHistoryFitsViewport,
    filteredLeadHistoryItems.length,
    scrollContainerRef,
    updateScrollState,
  ]);

  const showEmpty =
    !isMessagesLoading &&
    isLeadMessageHistoryInitialized &&
    isLeadMessageHistoryCurrent &&
    filteredLeadHistoryItems.length === 0;
  const showScrollToBottom =
    hasScrollableContent && !isAtBottom && !isMessagesLoading;
  const showOlderLoadError =
    olderHistoryLoadFailed && !isMessagesLoading && !isOlderHistoryLoading;
  const showLoadAllHistory =
    isLeadMessageHistoryInitialized &&
    canLoadOlderHistory &&
    !isMessagesLoading &&
    !isOlderHistoryLoading &&
    !showOlderLoadError;

  const isFilteredToEmpty = selectedLeadHistoryItems.length > 0;
  const hiddenByFiltersCount =
    selectedLeadHistoryItems.length - filteredLeadHistoryItems.length;
  const showHistoryStart =
    isHistoryFullyLoaded && filteredLeadHistoryItems.length > 0;

  const emptyDescription = isFilteredToEmpty
    ? 'Нет сообщений по выбранным фильтрам'
    : selectedLead?.IsNoCommunication ?? false
    ? 'С этим лидом не было переписки'
    : 'История клиента отсутствует';

  return (
    <div
      className={cn('LeadMessageList', {
        'LeadMessageList--with-pinned-audio': hasPinnedAudio,
      })}
    >
      <LeadPinnedAudioPlayer scrollContainerRef={scrollContainerRef} />
      <div
        className="LeadMessageList__scroll"
        ref={scrollContainerRef}
        onClick={handleReplyClick}
        onScroll={handleScroll}
      >
        {showEmpty ? (
          <div className="LeadMessageList__empty">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={emptyDescription}
            />
          </div>
        ) : (
          <div className="LeadMessageList__items">
            {aheadPinnedCommentItems.length > 0 && (
              <div className="LeadMessageList__pinned-ahead">
                {aheadPinnedCommentItems.map((item) => (
                  <LeadCommentMessage
                    key={getLeadHistoryStableKey(item)}
                    item={item}
                    isDetached
                  />
                ))}
              </div>
            )}
            {showHistoryStart && (
              <div className="LeadMessageList__history-start">
                Начало переписки
              </div>
            )}
            {hiddenByFiltersCount > 0 && (
              <div className="LeadMessageList__filters-hint">
                Часть сообщений скрыта фильтрами
              </div>
            )}
            {flowItems.map((item, index) => (
              <LeadMessageItem
                key={getLeadHistoryStableKey(item)}
                item={item}
                isFirstInGroup={groupFlags[index]?.isFirstInGroup ?? true}
                isLastInGroup={groupFlags[index]?.isLastInGroup ?? true}
                isReadPending={Boolean(
                  item.ChatMessages?.[0]?.RecommendChat &&
                    pendingKeys.has(
                      getRecommendMessageKey(
                        item.ChatMessages[0].RecommendChat.conversationId,
                        item.ChatMessages[0].RecommendChat.messageId
                      )
                    )
                )}
                onReadElement={registerReadElementHandler}
              />
            ))}
          </div>
        )}
      </div>
      {readError && (
        <div className="LeadMessageList__read-error" role="status">
          {readError}
          <button type="button" onClick={retryReadHandler}>
            Повторить
          </button>
        </div>
      )}
      {showLoadAllHistory && (
        <button
          type="button"
          className="LeadMessageList__load-all"
          onClick={() => void loadAllHistory()}
          disabled={isAllHistoryLoading}
        >
          {isAllHistoryLoading && <Spin size="small" />}
          {isAllHistoryLoading ? 'Загрузка истории…' : 'Загрузить всю историю'}
        </button>
      )}
      {isOlderHistoryLoading && (
        <div className="LeadMessageList__older-loading">
          <Spin size="small" />
        </div>
      )}
      {showOlderLoadError && (
        <div className="LeadMessageList__load-error">
          Не удалось загрузить сообщения
          <button
            type="button"
            className="LeadMessageList__load-error-retry"
            onClick={() => void loadOlderHistory()}
          >
            Повторить
          </button>
        </div>
      )}
      {showScrollToBottom && (
        <button
          type="button"
          className="LeadMessageList__scroll-to-bottom"
          onClick={scrollToBottomClickHandler}
          aria-label="Прокрутить вниз"
        >
          <DownOutlined />
        </button>
      )}
    </div>
  );
};
